import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Building2, AlertCircle } from 'lucide-react'
import { Typography } from '~/components/common/Typography'
import { Button } from '~/components/common/Button'
import { SettingsSection } from '~/components/common/SettingsSection'
import { DeleteConfirmModal } from './components/DeleteConfirmModal'
import CodingGuidelinesUpload from './components/organization/CodingGuidelinesUpload'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { changeOrgName, deleteWorkspace } from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'
import { isWorkspaceAdmin } from '~/utils/userUtils'
import { cm } from '~/utils/utils'
import { isDefaultWorkspace } from '~/utils/workspaceUtils'
import { MembersSection } from './components/organization/MembersSection'
import { RepoCacheTTLSection } from './components/organization/RepoCacheTTLSection'
import SSOConfigurationsSection from './components/organization/SSOConfigurationsSection'
import { SessionTimeoutSection } from './components/organization/SessionTimeoutSection'
import { XmlSettingsSection } from './components/organization/XmlSettingsSection'
import styles from './settings.module.scss'

const timeOptions: Record<string, number> = {
  '5 min': 5,
  '20 min': 20,
  '30 min': 30,
  '1 hour': 60,
  '2 hours': 120,
  '6 hours': 360,
  '12 hours': 720,
  '1 day': 1440,
  '3 days': 4320,
  '7 days': 10080,
  '30 days': 43200
}

export default function WorkspaceSettingsTab() {
  const {
    workspaces,
    currentWorkspace,
    isLoading: loading,
    refetch: refetchWorkspaces
  } = useWorkspaceContext()

  const [orgName, setOrgName] = useState(currentWorkspace?.name || '')
  const [isEditingName, setIsEditingName] = useState(false)
  const [SSOClientID, setSSOClientID] = useState(
    currentWorkspace?.org_metadata_sso?.sso_client_id || ''
  )
  const [SSOClientSecret, setSSOClientSecret] = useState(
    currentWorkspace?.org_metadata_sso?.sso_client_secret || ''
  )
  const [SSOTenantID, setSSOTenantID] = useState(
    currentWorkspace?.org_metadata_sso?.sso_tenant_id || ''
  )
  const [sessionTimeoutTime, setSessionTimeoutTime] = useState('30 days')
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { mutate: updateOrgName, isPending: isUpdatingName } = useMutation({
    mutationFn: changeOrgName,
    onSuccess: async () => {
      await refetchWorkspaces()
      setIsEditingName(false)
      BetterToast.success('Name Updated')
    },
    onError: (error: Error) => {
      BetterToast.error('Failed to update name: ' + error.message)
      setIsEditingName(false)
    }
  })

  useEffect(() => {
    if (!currentWorkspace) return

    setOrgName(currentWorkspace.name)
    setIsEditingName(false)
    setSSOClientID(currentWorkspace.org_metadata_sso?.sso_client_id || '')
    setSSOClientSecret(currentWorkspace.org_metadata_sso?.sso_client_secret || '')
    setSSOTenantID(currentWorkspace.org_metadata_sso?.sso_tenant_id || '')

    const timeoutValue = currentWorkspace.org_session_timeout
    const timeoutKey =
      Object.keys(timeOptions).find(key => timeOptions[key] === timeoutValue) || '30 days'
    setSessionTimeoutTime(timeoutKey)
  }, [currentWorkspace])

  if (loading || !currentWorkspace) {
    return <div className='select-none'>Loading organization data...</div>
  }

  const org_id_base64 = btoa((currentWorkspace.id || 0).toString())
  const { origin } = new URL(location.href)
  const ssoOrgLink = `${origin}/sso/${org_id_base64}/microsoft/login`
  const callBackLink = `${origin}/sso/${org_id_base64}/microsoft/callback`
  const isCurrentWorkspaceDefault = isDefaultWorkspace(currentWorkspace)
  const isAdmin = isWorkspaceAdmin(currentWorkspace)
  const canDeleteWorkspace = isAdmin && !isCurrentWorkspaceDefault

  const handleOrgNameBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.value.trim()

    if (name === currentWorkspace.name) {
      setIsEditingName(false)
      return
    }

    if (name.length === 0) {
      BetterToast.error('Organization name cannot be empty')
      setOrgName(currentWorkspace.name || '')
      setIsEditingName(false)
      return
    }

    if (name.length > 255) {
      BetterToast.error('Organization name cannot exceed 255 characters')
      setOrgName(currentWorkspace.name || '')
      setIsEditingName(false)
      return
    }

    if (!currentWorkspace.uuid) {
      BetterToast.error('Workspace UUID not found')
      setIsEditingName(false)
      return
    }

    updateOrgName({ workspaceUuid: currentWorkspace.uuid, org_name: name })
  }

  const handleDeleteWorkspace = async () => {
    if (isCurrentWorkspaceDefault) {
      BetterToast.error('Cannot delete default organization')
      return
    }

    if (!isAdmin) {
      BetterToast.error('Only workspace administrators can delete this workspace')
      return
    }

    if (!currentWorkspace.uuid) {
      BetterToast.error('Workspace UUID not found')
      return
    }

    const remainingWorkspaces = workspaces.filter(ws => ws.id !== currentWorkspace.id)

    try {
      await deleteWorkspace(currentWorkspace.uuid)
      BetterToast.success('Workspace deleted successfully')

      if (remainingWorkspaces.length > 0) {
        window.location.href = `/w/${remainingWorkspaces[0].uuid}`
      } else {
        window.location.href = '/'
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      BetterToast.error('Delete failed: ' + errorMessage)
    }
  }

  return (
    <>
      <div className={cm('w-full h-full overflow-auto pb-32', styles['no-scroll-bar'])}>
        <div className='flex flex-col gap-9'>
          {/* Workspace Name */}
          <SettingsSection>
            <div className='flex items-center justify-between h-12'>
              <div className='flex items-center gap-4'>
                <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
                  <Building2 className='w-[18px] h-[18px]' />
                </span>
                <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
                  Workspace Name
                </span>
              </div>
              <div className='flex items-center gap-2'>
                {isEditingName ? (
                  <input
                    type='text'
                    value={orgName}
                    disabled={isUpdatingName}
                    onChange={e => setOrgName(e.target.value)}
                    onBlur={handleOrgNameBlur}
                    onKeyDown={e => {
                      if (e.key === 'Enter') e.currentTarget.blur()
                      if (e.key === 'Escape') {
                        setOrgName(currentWorkspace.name || '')
                        setIsEditingName(false)
                      }
                    }}
                    autoFocus
                    className='w-[240px] h-12 py-2 px-3 border border-tokens-status-link-1 rounded-xl text-right font-mono font-semibold text-[15px] leading-normal text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis bg-tokens-neutral-bg-white outline-none shadow-[0px_2px_4px_rgba(0,0,0,0.02),0px_1px_6px_-1px_rgba(0,0,0,0.02),0px_1px_2px_rgba(0,0,0,0.03)]'
                    aria-label='Workspace name input'
                  />
                ) : (
                  <div className='relative group'>
                    <button
                      type='button'
                      onClick={() => isAdmin && setIsEditingName(true)}
                      className={cm(
                        'py-2 px-3 font-mono font-semibold text-[15px] leading-normal text-right text-[rgba(0,0,0,0.65)] overflow-hidden text-ellipsis whitespace-nowrap',
                        isAdmin && 'cursor-pointer hover:opacity-70 transition-opacity'
                      )}
                      disabled={!isAdmin}
                    >
                      {orgName}
                    </button>
                    {isAdmin && (
                      <div className='absolute -bottom-8 right-0 px-2 py-1 bg-tokens-neutral-bg-action text-tokens-neutral-text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 select-none'>
                        Click to edit
                      </div>
                    )}
                  </div>
                )}
                {isUpdatingName && (
                  <Typography
                    fontFamily='geist'
                    fontSize='labelSm'
                    className='text-tokens-neutral-text-quaternary select-none'
                  >
                    Updating...
                  </Typography>
                )}
              </div>
            </div>
          </SettingsSection>

          <MembersSection />

          {/* Runtime Settings (Maven, Java, Mule + Upload XML) */}
          <XmlSettingsSection />

          {/* Upload Coding Guidelines */}
          <CodingGuidelinesUpload />

          {isAdmin && (
            <>
              {/* Session Timeout */}
              <SessionTimeoutSection
                sessionTimeoutTime={sessionTimeoutTime}
                setSessionTimeoutTime={setSessionTimeoutTime}
              />

              {/* SSO Configuration */}
              <SSOConfigurationsSection
                ssoOrgLink={ssoOrgLink}
                callBackLink={callBackLink}
                SSOClientID={SSOClientID}
                setSSOClientID={setSSOClientID}
                SSOClientSecret={SSOClientSecret}
                setSSOClientSecret={setSSOClientSecret}
                SSOTenantID={SSOTenantID}
                setSSOTenantID={setSSOTenantID}
              />

              <RepoCacheTTLSection />
            </>
          )}

          {/* Delete Workspace - Danger Zone */}
          {canDeleteWorkspace && (
            <>
              <div className='pt-12'>
                <div className='border-t border-tokens-neutral-border-2' />
              </div>
              <div className='flex items-center gap-4 bg-tokens-status-error-10 rounded-3xl px-6 py-8'>
                <span className='flex items-center justify-center w-6 h-6 shrink-0'>
                  <AlertCircle className='w-[18px] h-[18px] text-tokens-status-error-text' />
                </span>
                <div className='flex flex-col gap-1 flex-1 min-w-0'>
                  <span className='font-sans font-medium text-[14px] leading-[22px] text-tokens-status-error-text select-none'>
                    Delete Workspace
                  </span>
                  <span className='font-sans font-medium text-[12px] leading-[16px] text-[rgba(234,70,45,0.7)] select-none'>
                    Permanently delete this workspace and all its data. This action cannot be
                    undone.
                  </span>
                </div>
                <Button
                  variant='destructive'
                  onClick={() => setShowDeleteModal(true)}
                  className='select-none'
                >
                  Delete Workspace
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          setShowDeleteModal(false)
          handleDeleteWorkspace()
        }}
        title='Delete Workspace?'
        description={`Are you sure you want to delete "${orgName}"? This action cannot be undone and will permanently delete all workspace data, including:`}
        itemsToDelete={[
          'All projects and files',
          'Member access and permissions',
          'XML settings',
          'Coding guidelines and settings'
        ]}
        confirmButtonText='Remove Workspace'
      />
    </>
  )
}
